import Head from 'next/head';
import React from 'react';
import type { Thing, WithContext } from 'schema-dts';

type JsonLdProps<T extends Thing> = {
    data: WithContext<T> | WithContext<T>[];
    scriptKey?: string;
    inBody?: boolean;
};

function stringifyJsonLd<T extends Thing>(data: WithContext<T> | WithContext<T>[]): string | null {
    if (Array.isArray(data)) {
        if (data.length === 0) return null;
        const context = (data[0] as WithContext<T>)['@context'];
        const graph = data.map((item) => {
            const node = { ...(item as unknown as Record<string, unknown>) };
            delete node['@context'];
            return node;
        });
        return JSON.stringify({ '@context': context, '@graph': graph });
    }
    return JSON.stringify(data);
}

/**
 * JSON-LD component
 *
 * Renders JSON-LD in document head by default. Pass `inBody` to render the script
 * at the call site (before `</body>` when placed after layout chrome).
 *
 * @example
 * <JsonLd<Person> data={{
 *     "@context": "https://schema.org",
 *     "@type": "Person",
 *     "name": "John Doe",
 * }} />
 */
const JsonLd = <T extends Thing>({ data, scriptKey = 'json-ld', inBody = false }: JsonLdProps<T>) => {
    const json = stringifyJsonLd(data);
    if (!json) return null;

    const script = <script key={scriptKey} type="application/ld+json" dangerouslySetInnerHTML={{ __html: json }} />;

    if (inBody) return script;

    return <Head>{script}</Head>;
};

export default JsonLd;
